import React from "react";
import { useQuery } from "@tanstack/react-query";
import styles from "./styles.module.css";
import { HEADLINE_SIZE, Newsletter as NewsletterType } from "~types/types";
import { fetchNewsletterFromApi } from "~api/fetchNewsletterFromApi";
import LazyRetinaImage from "~components/LazyRetinaImage";
import Headline from "~components/Headline";
import Summary from "~components/Summary";
import { NewsletterPlaceholder } from "~components/Newsletter/NewsletterPlaceholder";
import { NewsletterForm } from "~components/NewsletterForm";
import { PlaceholderWithError } from "~components/PlaceholderWithError";

type NewsletterProps = {
  title?: string;
  id: string;
  embedUrl?: string;
  countryCode: string;
};

const SUBSCRIBER_ONLY = "subscriberOnly";

enum NewsletterImagePlacement {
  Default = "card.default",
  Mobile = "card.mobile",
  Standalone = "card.standalone",
}

const getNewsletterImage = (newsletter?: NewsletterType) => {
  const mobileImage = newsletter?.images?.find((img) =>
    img?.placement?.includes(NewsletterImagePlacement.Mobile)
  );
  const defaultImage = newsletter?.images?.find((img) =>
    img?.placement?.includes(NewsletterImagePlacement.Default)
  );

  return mobileImage || defaultImage;
};

export default function Newsletter(props: NewsletterProps) {
  const { id, countryCode, embedUrl = "" } = props;

  // fetch newsletter from api using tanstack query
  const {
    status: fetchNewsletterStatus,
    data: newsletterData,
    refetch: fetchNewsletter,
  } = useQuery({
    queryKey: ["newsletter", embedUrl],
    queryFn: () => fetchNewsletterFromApi(embedUrl),
    // The query will not execute until the embed url exists
    enabled: embedUrl.length > 1,
  });

  if (fetchNewsletterStatus === "pending") {
    return <NewsletterPlaceholder />;
  }

  if (fetchNewsletterStatus === "error") {
    return (
      <div className={styles.newsletter}>
        <PlaceholderWithError
          errorMessage="There was an error retrieving newsletter data."
          placeholder={<NewsletterPlaceholder />}
          onRetryClick={() => fetchNewsletter({ throwOnError: true })}
        />
      </div>
    );
  }

  const image = getNewsletterImage(newsletterData);
  const title = props.title ?? newsletterData.title;
  const summary = newsletterData?.description;
  const listId = newsletterData?.signUpList;
  const sampleUrl = newsletterData?.sampleUrl;
  const isSubscriberOnly = newsletterData?.subscriptionType === SUBSCRIBER_ONLY;

  return (
    <section className={styles.newsletter}>
      <div className={styles.container}>
        <LazyRetinaImage
          image={image}
          desktopMaxWidth={141}
          mobileMaxWidth={96}
          className={styles.image}
          aspectRatio={1}
        />
        <div className={styles.contentContainer}>
          {isSubscriberOnly && (
            <p className={styles.subscriberOnlyLabel}>Subscriber Only</p>
          )}
          {title ? <Headline text={title} size={HEADLINE_SIZE.SMALL} /> : null}
          <Summary summary={summary} removeSummaryBottomMargin />
          {sampleUrl ? (
            <a
              target="_blank"
              href={newsletterData?.sampleUrl}
              className={styles.previewButton}
            >
              Preview
            </a>
          ) : null}
        </div>
      </div>

      <NewsletterForm
        countryCode={countryCode}
        isSubscriberOnly={isSubscriberOnly}
        listId={listId}
        id={id}
      />
    </section>
  );
}
